/**
 * Looks — share, copy link, and optional product quick-view modal.
 * Modal is enabled only when `.looks[data-looks-product-modal="true"]` is present
 * (article Looks template). Blog index omits the attribute — product links navigate normally.
 */
(function () {
  const SELECTORS = {
    share: '.js-lookShare',
    copy: '.js-lookCopy',
    copyRegion: '.js-looksPostCopy',
    modalWrap: '#looks-modal',
    modalGallery: '.js-looksModalGallery',
    addButton: '.js-modalAddToBag',
    qty: '.js-modalQty',
    qtyMinus: '.js-modalQtyMinus',
    qtyPlus: '.js-modalQtyPlus',
  };

  const state = {
    swiper: null,
    config: { moneyFormat: '${{amount}}', swatches: '' },
    originalCopyLabels: new WeakMap(),
  };

  function readConfig() {
    const el = document.getElementById('looks-post-config');
    if (!el) return;
    try {
      state.config = { ...state.config, ...JSON.parse(el.textContent) };
    } catch (e) {
      console.warn('looks-post: invalid config JSON', e);
    }
  }

  function formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const fmt = state.config.moneyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise((resolve, reject) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '0';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        if (document.execCommand('copy')) resolve();
        else reject(new Error('copy failed'));
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(textArea);
      }
    });
  }

  function syncCartAfterAdd() {
    return new Promise((resolve) => {
      if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
        window.liquidAjaxCart.update({}, { lastCallback: () => resolve() });
        return;
      }
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const count = cart.item_count ?? 0;
          document.querySelectorAll('[data-ajax-cart-bind="item_count"]').forEach((el) => {
            el.textContent = String(count);
            el.classList.toggle('hidec', count === 0);
            el.classList.toggle('showc', count > 0);
          });
        })
        .catch(() => {})
        .finally(resolve);
    });
  }

  function refreshCartSections() {
    const jobs = Array.from(document.querySelectorAll('[data-ajax-cart-section]')).map((cartEl) => {
      const section = cartEl.closest('[id^="shopify-section-"]');
      if (!section) return Promise.resolve();
      const sid = section.id.replace('shopify-section-', '');
      return fetch(`${window.location.pathname}?sections=${sid}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data[sid]) return;
          const doc = new DOMParser().parseFromString(data[sid], 'text/html');
          const fresh = doc.querySelector('[data-ajax-cart-section]');
          if (fresh) cartEl.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
    return Promise.all(jobs);
  }

  function addToCart(variantId, quantity) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }] }),
    }).then((res) => {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    });
  }

  function onShareClick(ev) {
    ev.preventDefault();
    const el = ev.currentTarget;
    const url = el.getAttribute('data-url');
    const title = el.getAttribute('data-tile') || document.title;
    if (!navigator.share) return;
    navigator.share({ title, url }).catch((err) => {
      if (err && err.name !== 'AbortError') console.error('Share failed', err);
    });
  }

  function onCopyClick(ev) {
    ev.preventDefault();
    const el = ev.currentTarget;
    const url = el.getAttribute('data-url');
    const successCopy = el.getAttribute('data-success');
    if (!state.originalCopyLabels.has(el)) {
      state.originalCopyLabels.set(el, el.textContent);
    }
    copyToClipboard(url)
      .then(() => {
        if (successCopy) {
          el.setAttribute('aria-live', 'polite');
          el.textContent = successCopy;
        }
      })
      .catch((err) => {
        console.error(err);
        el.setAttribute('aria-live', 'polite');
        el.textContent = 'Error';
      });
  }

  function getSwatchColor(name) {
    const raw = state.config.swatches || '';
    if (!raw || !name) return null;
    const lines = raw.split(/<br\s*\/?>|\n/).map((s) => s.trim()).filter(Boolean);
    const match = lines
      .map((line) => {
        const parts = line.split('==');
        return { name: (parts[0] || '').trim(), color: (parts[1] || '').trim() };
      })
      .find((s) => s.name.toLowerCase() === String(name).toLowerCase());
    return match || null;
  }

  function truncateDescription(html, maxWords) {
    const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return `${words.slice(0, maxWords).join(' ')}...`;
  }

  function getVariantImages(product, variant) {
    const images = product.images || [];
    if (!variant || !variant.featured_image) return images;
    const featuredSrc = variant.featured_image.src || variant.featured_image;
    const startIdx = images.findIndex((img) => (img.src || img) === featuredSrc);
    if (startIdx < 0) return images.length ? images : [{ src: featuredSrc, alt: product.title }];
    let endIdx = images.length;
    for (let i = startIdx + 1; i < images.length; i += 1) {
      const imgSrc = images[i].src || images[i];
      const belongsToOther = (product.variants || []).some(
        (v) => v.id !== variant.id && v.featured_image && (v.featured_image.src || v.featured_image) === imgSrc
      );
      if (belongsToOther) {
        endIdx = i;
        break;
      }
    }
    return images.slice(startIdx, endIdx);
  }

  function buildAddButton(variant, productTitle) {
    const available = variant.available;
    const price = formatMoney(variant.price);
    return `<button type="button" class="purchase-bar__button purchase-bar__button--modal button button--${available ? 'black' : 'gray'} js-modalAddToBag" data-id="${variant.id}" ${available ? '' : 'disabled'}>
      <span>${available ? 'Add to cart' : 'Sold out'}</span>
      <span class="purchase-bar__price"><span class="purchase-bar__price__regular">${price}</span></span>
    </button>`;
  }

  function buildQtyMarkup() {
    return `<div class="purchase-bar__qty purchase-bar__qty--modal">
      <button type="button" class="purchase-bar__qty__minus js-modalQtyMinus"><span class="visually-hidden">Decrease quantity.</span></button>
      <span><i class="visually-hidden">Current quantity:</i><i class="js-modalQty" aria-live="polite">1</i></span>
      <button type="button" class="purchase-bar__qty__plus js-modalQtyPlus"><span class="visually-hidden">Increase quantity.</span></button>
    </div>`;
  }

  function renderModal(product, variant) {
    const wrap = document.querySelector(SELECTORS.modalWrap);
    if (!wrap) return;

    const images = getVariantImages(product, variant);
    const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    const shadeIdx = optionNames.findIndex((n) => String(n).toLowerCase() === 'shade');
    const nameIdx = optionNames.findIndex((n) => String(n).toLowerCase() === 'name');
    const colorName = shadeIdx >= 0 ? variant[`option${shadeIdx + 1}`] : '';
    const variantName = nameIdx >= 0 ? variant[`option${nameIdx + 1}`] : '';
    const variants = product.variants || [];
    const variantIndex = variants.findIndex((v) => v.id === variant.id);
    const displayIndex = variantIndex < 9 ? `0${variantIndex + 1}` : String(variantIndex + 1);

    const weightTag = (product.tags || []).find((t) => String(t).indexOf('weight_') === 0);
    let weightCopy = '';
    if (weightTag) {
      const weight = Number(weightTag.split('_').pop());
      if (!Number.isNaN(weight)) {
        weightCopy = `(${weight} g / ${(weight * 0.035274).toFixed(2)} oz)`;
      }
    }

    const swatch = getSwatchColor(colorName);
    const hasBg = !!(swatch && swatch.color);
    const hasBgImg = hasBg && swatch.color.indexOf('http') === 0;
    const description = truncateDescription(product.body_html || product.description || '', 20);
    const button = buildAddButton(variant, product.title);
    const slides = (images.length ? images : [{ src: '', alt: product.title }])
      .map((img) => {
        const src = img.src || img;
        const alt = img.alt || product.title;
        const url = src.includes('?') ? `${src}&width=836` : `${src}?width=836`;
        const url2x = src.includes('?') ? `${src}&width=836` : `${src}?width=836`;
        return `<div class="swiper-slide">
          <img alt="${alt.replace(/"/g, '&quot;')}" src="${url.replace(/"/g, '&quot;')}" srcset="${url2x.replace(/"/g, '&quot;')} 2x" loading="lazy" width="418" height="542">
        </div>`;
      })
      .join('');

    const swatchHtml =
      hasBg &&
      `<div class="looks__modal__swatch" style="${hasBgImg ? `background-image:url(${swatch.color})` : `background-color:${swatch.color}`}"></div>`;

    wrap.innerHTML = `
      <div class="looks__modal" role="dialog" aria-modal="true" aria-labelledby="looks-modal-title">
        <div class="looks__modal__images swiper js-looksModalGallery">
          <div class="swiper-wrapper">${slides}</div>
          <button type="button" class="looks__modal__nav looks__modal__nav--prev swiper-button-prev" aria-label="Previous slide"><span class="visually-hidden">Previous</span></button>
          <button type="button" class="looks__modal__nav looks__modal__nav--next swiper-button-next" aria-label="Next slide"><span class="visually-hidden">Next</span></button>
        </div>
        <div class="looks__modal__content">
          <div class="looks__modal__header">
            <div id="looks-modal-title" class="looks__modal__title">
              <strong>${product.title.replace(/</g, '&lt;')}</strong>
              ${variantName ? variantName.replace(/</g, '&lt;') : ''}
            </div>
            <div class="looks__modal__add-mobile">${button}</div>
          </div>
          <div class="looks__modal__weight">${weightCopy}</div>
          <div class="looks__modal__copy">${description.replace(/</g, '&lt;')}</div>
          <div class="looks__modal__color">${swatchHtml || ''}${displayIndex} ${colorName.replace(/</g, '&lt;')}</div>
          <div class="looks__modal__add-desktop">
            ${buildQtyMarkup()}
            ${button}
          </div>
          <a href="/products/${product.handle}?variant=${variant.id}" class="looks__modal__details"><u>View Detail Page</u></a>
          <button type="button" class="looks__modal__close" aria-label="Close modal" data-looks-modal-close></button>
        </div>
      </div>
      <button type="button" class="looks__modal__bg" aria-label="Close modal" data-looks-modal-close></button>`;

    wrap.classList.add('is-open');
    wrap.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('overflow-hidden');

    bindModalControls();
    initModalSwiper();
  }

  function destroyModalSwiper() {
    if (state.swiper && typeof state.swiper.destroy === 'function') {
      state.swiper.destroy(true, true);
      state.swiper = null;
    }
  }

  function initModalSwiper() {
    destroyModalSwiper();
    const el = document.querySelector(SELECTORS.modalGallery);
    if (!el || typeof window.Swiper !== 'function') return;
    state.swiper = new window.Swiper(el, {
      centeredSlides: true,
      loop: (el.querySelectorAll('.swiper-slide').length || 0) > 1,
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
      slidesPerView: 'auto',
      threshold: 5,
    });
  }

  function closeModal() {
    const wrap = document.querySelector(SELECTORS.modalWrap);
    if (!wrap) return;
    destroyModalSwiper();
    wrap.classList.remove('is-open');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML = '';
    document.documentElement.classList.remove('overflow-hidden');
  }

  function getModalQty() {
    const el = document.querySelector(SELECTORS.qty);
    return el ? Math.max(1, parseInt(el.textContent, 10) || 1) : 1;
  }

  function setModalQty(qty) {
    const value = Math.max(1, qty);
    document.querySelectorAll(SELECTORS.qty).forEach((el) => {
      el.textContent = String(value);
    });
  }

  function bindModalControls() {
    document.querySelectorAll('[data-looks-modal-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    document.querySelectorAll(SELECTORS.addButton).forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        const id = ev.currentTarget.getAttribute('data-id');
        if (!id || ev.currentTarget.disabled) return;
        const qty = getModalQty();
        ev.currentTarget.disabled = true;
        addToCart(id, qty)
          .then(() => syncCartAfterAdd())
          .then(() => refreshCartSections())
          .then(() => {
            window.dispatchEvent(new CustomEvent('cart-open'));
          })
          .catch((err) => console.error(err))
          .finally(() => {
            ev.currentTarget.disabled = false;
          });
      });
    });

    document.querySelectorAll(SELECTORS.qtyPlus).forEach((el) => {
      el.addEventListener('click', () => setModalQty(getModalQty() + 1));
    });

    document.querySelectorAll(SELECTORS.qtyMinus).forEach((el) => {
      el.addEventListener('click', () => setModalQty(getModalQty() - 1));
    });
  }

  function parseProductHref(href) {
    if (!href || href.indexOf('/products/') === -1) return null;
    try {
      const url = new URL(href, window.location.origin);
      const pathParts = url.pathname.split('/').filter(Boolean);
      const productsIdx = pathParts.indexOf('products');
      if (productsIdx === -1 || !pathParts[productsIdx + 1]) return null;
      const handle = pathParts[productsIdx + 1];
      const variantId = url.searchParams.get('variant');
      return { handle, variantId };
    } catch (e) {
      return null;
    }
  }

  function onProductLinkClick(ev) {
    const anchor = ev.target.closest('a[href*="/products/"]');
    if (!anchor || !anchor.closest(SELECTORS.copyRegion)) return;

    const parsed = parseProductHref(anchor.getAttribute('href'));
    if (!parsed) return;

    ev.preventDefault();
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}products/${parsed.handle}.js`, { headers: { Accept: 'application/json' } })
      .then((res) => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then((product) =>
        // Variants hidden via the custom.hide_from_online_store metafield are
        // absent from the ?view=json-data payload; /products/{handle}.js cannot
        // expose metafields, so intersect the two. Fails open on any error.
        fetch(`${root}products/${parsed.handle}?view=json-data`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (data && Array.isArray(data.variants) && Array.isArray(product.variants)) {
              const hidden = {};
              data.variants.forEach((v) => { if (v && v.id != null && v.hidden === true) hidden[String(v.id)] = true; });
              const filtered = product.variants.filter((v) => !hidden[String(v.id)]);
              if (filtered.length) product.variants = filtered;
            }
            return product;
          })
          .catch(() => product)
      )
      .then((product) => {
        let variant = product.variants[0];
        if (parsed.variantId) {
          const found = product.variants.find((v) => String(v.id) === String(parsed.variantId));
          if (found) variant = found;
        } else {
          const available = product.variants.find((v) => v.available);
          if (available) variant = available;
        }
        renderModal(product, variant);
      })
      .catch((err) => console.error('looks-post modal:', err));
  }

  function onDocumentKeydown(ev) {
    if (ev.key === 'Escape') {
      const wrap = document.querySelector(SELECTORS.modalWrap);
      if (wrap && wrap.classList.contains('is-open')) closeModal();
    }
  }

  function initShareUi() {
    const canShare = !!(navigator && navigator.share);
    document.body.classList[canShare ? 'add' : 'remove']('state--can-share');

    document.querySelectorAll(SELECTORS.share).forEach((el) => {
      el.addEventListener('click', onShareClick);
    });

    document.querySelectorAll(SELECTORS.copy).forEach((el) => {
      el.addEventListener('click', onCopyClick);
    });
  }

  function isProductModalEnabled() {
    return !!document.querySelector('.looks[data-looks-product-modal="true"]');
  }

  function initProductLinks() {
    document.querySelectorAll(SELECTORS.copyRegion).forEach((region) => {
      region.addEventListener('click', onProductLinkClick);
    });
  }

  function init() {
    readConfig();
    initShareUi();
    if (isProductModalEnabled()) {
      initProductLinks();
      document.addEventListener('keydown', onDocumentKeydown);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
